/**
 * Publish-composer types.
 *
 * The composer is the "make a real app out of your model" surface
 * that opens after training. The kid picks one of a handful of
 * templates, tweaks colour + text + per-class flair, then either
 * downloads a self-contained HTML bundle or publishes to a real URL
 * on bryta.app. Customisation is bounded — fixed templates, fixed
 * palettes, single-emoji per class — so the moderation surface stays
 * tight and the build pipeline stays small.
 */

import type { ProjectKind } from '../data/types';

export type TemplateId = 'sticker-cam' | 'class-quiz' | 'sound-reactor';

export type PublishThemeId = 'lab' | 'paper' | 'riso' | 'sunset' | 'ocean';

/** Stable ids for the optional widgets the kid can layer onto the
 *  published page. These match the `widgets` flag keys 1:1, plus a
 *  pseudo "chips" slot that always renders. The composer uses these
 *  ids for both the visibility toggle and the order persistence. */
export type WidgetId =
  | 'bigBanner'
  | 'scoreStreak'
  | 'historyStrip'
  | 'confidenceBars'
  | 'qrCode'
  | 'shareLinks'
  | 'soundOnMatch';

/** Default top-down order of visible widgets in a freshly-created
 *  composer. Tuned to put the dramatic bits first (banner, score) and
 *  the calmer info widgets last. soundOnMatch has no visual so it is
 *  excluded — it only affects audio output. */
export const DEFAULT_WIDGET_ORDER: ReadonlyArray<WidgetId> = [
  'bigBanner',
  'scoreStreak',
  'historyStrip',
  'confidenceBars',
  'qrCode',
  'shareLinks',
];

/** Per-template-and-kid customisation slots. Optional everywhere so a
 *  freshly-picked template renders sensibly without any kid input. */
export interface PerClassCustomisation {
  /** Single emoji shown when this class fires (sticker-cam) or as
   *  the visual cue (class-quiz / sound-reactor). Validated to a
   *  single grapheme cluster at edit time. */
  emoji?: string;
  /** Override the class name shown to viewers. The model still uses
   *  the kid's training-time class name internally. */
  label?: string;
  /** Curated sound id played when this class fires. Empty/absent =
   *  silent. The library lives in `sounds.ts`. */
  soundId?: string;
}

export interface PublishConfig {
  template: TemplateId;
  themeId: PublishThemeId;
  /** Index into the template's `layoutVariants` array. */
  layoutVariant: number;
  text: {
    title: string;
    subtitle?: string;
    actionLabel?: string;
  };
  perClass: Record<string /* classId */, PerClassCustomisation>;
  branding: {
    /** "Made on Bryta" footer link. Default true; staying on for M1
     *  to keep the brand attribution visible while the gallery
     *  matures. */
    showFooter: boolean;
  };
  /** Optional widgets the kid can layer onto the page. Empty / all-
   *  false renders a clean stage with just the emoji bursts.
   *  All widgets are off by default — kids opt in. */
  widgets?: {
    /** Live confidence-bars panel — see *how* sure the model is. */
    confidenceBars?: boolean;
    /** Big result banner above the stage — "Looks like Dog 🐶" when
     *  confidence ≥ minScore. Accessibility win for low-vision
     *  viewers; more dramatic for share-to-grandma. */
    bigBanner?: boolean;
    /** Floating QR code linking to the share URL. "Scan with a
     *  phone to try it" — great for showing off in person. */
    qrCode?: boolean;
    /** Counter that increments each time the model fires above
     *  threshold. Lives client-side per viewer (resets on reload). */
    scoreStreak?: boolean;
    /** Plays a short tone when the prediction crosses the threshold,
     *  cool-down so it doesn't loop on a steady prediction. */
    soundOnMatch?: boolean;
    /** Scrolling strip of the last few predictions with their emoji.
     *  Keeps the page feeling alive even between bursts. */
    historyStrip?: boolean;
    /** "Send this to a friend" cluster — Copy link / native share /
     *  email. Tightens the show-off loop beyond the QR code. */
    shareLinks?: boolean;
  };
  /** Order the visible widgets render in, top → bottom. Only ids that
   *  resolve to a visible widget are honoured; unknown ids and missing
   *  ids are ignored. The composer lets the kid drag-reorder; this
   *  preserves their choice across publishes. */
  widgetOrder?: ReadonlyArray<WidgetId>;
  /** Bumped each time the kid edits the composer locally. Lets the
   *  publish endpoint detect a stale upload (kid kept editing while
   *  the publish was in flight) and reconcile sensibly. */
  version: number;
}

/** What the composer / catalogue knows about each template. Pure
 *  metadata — the actual render component lives next to its config
 *  in `publish/templates/`. */
export interface TemplateDef {
  id: TemplateId;
  /** Kid-facing name. Short, verb-y. */
  name: string;
  /** One-sentence description. Reads like a promise to the kid. */
  blurb: string;
  /** Project kinds this template applies to. The picker only shows
   *  templates whose kinds include the current project's kind. */
  supportedKinds: readonly ProjectKind[];
  /** A short list of layout variant labels (≥ 1). The composer
   *  surfaces these as small radio chips. Index = `layoutVariant`. */
  layoutVariants: readonly string[];
  /** Default starter copy. Composer pre-fills these; the kid can
   *  edit. */
  defaults: {
    title: string;
    subtitle?: string;
    actionLabel?: string;
  };
}

/** Hard length caps so the moderation surface stays bounded. The
 *  composer's text inputs enforce these client-side; the publish
 *  endpoint will re-enforce server-side when it ships. */
export const PUBLISH_TEXT_LIMITS = {
  title: 60,
  subtitle: 140,
  actionLabel: 20,
  perClassLabel: 20,
} as const;
